import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import { getCourseById, API_BASE } from "../Services/Api";
import { useCart } from "../Context/Cartcontext";
import { useAuth } from "../Context/AuthContext";

export default function AboutCourse() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, cartItems } = useCart();
  const { user } = useAuth();

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [popup, setPopup] = useState({ show: false, courseName: "" });



  useEffect(() => {
      window.scrollTo(0, 0);
    }, []);

  //  Validate id
  useEffect(() => {
    if (!id || !/^\d+$/.test(id)) {
      navigate("/NotFound", { replace: true });
    }
  }, [id, navigate]);

  //  Fetch all courses + enrolled courses
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);

    const fetchData = async () => {
  try {
    //  Fetch course using your direct API
    const result = await getCourseById(id);
    if (!mounted) return;

    if (!result) {
      setError("Course not found.");
    } else {
      setCourse(result);  // 🎉 Directly store course
    }

  } catch (err) {
    console.error(err);
    setError("Failed to load course details. Please try again.");
  } finally {
    if (mounted) setLoading(false);
  }
};

    fetchData();
    return () => {
      mounted = false;
    };
  }, [id]);

  //  Check if enrolled
  const enrolledObj = useMemo(() => {
    if (!user || !enrolledCourses.length) return null;
    return enrolledCourses.find(
      (c) => parseInt(c.main_course || c.course_id) === parseInt(id)
    );
  }, [user, enrolledCourses, id]);

  const isEnrolled = !!enrolledObj;
  const inCart = cartItems.some((item) => parseInt(item.id) === parseInt(id));

  //  Sanitize description
  const sanitizedDescription = useMemo(() => {
    if (!course?.course_short_desc) return "";
    return DOMPurify.sanitize(course.course_short_desc, {
      ALLOWED_TAGS: ["b", "i", "em", "strong", "p", "ul", "ol", "li", "a", "br", "span", "h1", "h2", "h3", "h4"],

      ALLOWED_ATTR: ["href", "target", "rel", "src", "alt", "title", "class", "style"],
    });
  }, [course?.course_short_desc]);

  //  Add to cart
  const handleAddToCart = async () => {
    if (!user) {
      navigate("/login", { state: { from: `/about-course/${id}` } });
      return;
    }
    if (inCart) {
      alert("Course already in cart!");
      return;
    }
    addToCart(course);
    setPopup({ show: true, courseName: course.course_name });
    setTimeout(() => setPopup({ show: false, courseName: "" }), 3000);
  };

  //  Handle Moodle access
  const handleGetAccess = async () => {
    if (!enrolledObj) return;
    try {
      const res = await fetch(`${API_BASE}/connect-course`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ courseId: enrolledObj.moodle_course_id }),
      });
      const html = await res.text();
      document.open();
      document.write(html);
      document.close();
    } catch (err) {
      console.error("Error connecting to course:", err);
    }
  };

  if (loading)
    return (
      <div className="text-center py-10">
        <p className="text-gray-600">Loading course details...</p>
      </div>
    );

  if (error)
    return (
      <div className="text-center py-10">
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-indigo-600 text-white rounded"
        >
          Retry
        </button>
      </div>
    );




  return (
    <>
    <helmet>
     <title>EDC - About Course</title>
      <meta name="description" content="Browse professional courses to upskill yourself in technology, business, and more." />
      <meta name="keywords" content="courses, learning, edc, education, skills" />
      <meta property="og:title" content="Courses - EDC Learning" />
      <meta property="og:description" content="Find the right course to upskill yourself." />
      <meta property="og:image" content="/images/LSClogo.png" />
      <meta property="og:url" content="https://edc.lsc-india.org/" />
      <meta name="twitter:card" content="summary_large_image" />
</helmet>

      <section
        className="bg-white text-center py-20 px-4"
        style={{ backgroundColor: "rgb(219 228 220 / 23%)" }}
      >
        <div className="max-w-4xl mx-auto">
          <h1
            className="text-4xl md:text-4xl font-extrabold text-gray-900 leading-tight"
            style={{ color: "rgb(93, 76, 207)" }}
          >
            {course?.course_name}
          </h1>

          <p className="text-gray-600 mt-6 text-base md:text-lg">
            Gain essential skills and knowledge through this industry-relevant course.
          </p>

          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mt-8">
            {!isEnrolled && (
              <form
                action="https://edcbackend.lsc-india.org/api/payment/pre-pay-process"
                method="POST"
                onSubmit={(e) => {
                  if (!user) {
                    e.preventDefault(); // STOP submit
                    navigate("/login"); // redirect to login
                  }
                }}
              >
                <input type="hidden" name="course_ids[]" value={course?.id} />
                 <input type="hidden" name="user_id" value={user?.id} /> 

                <button
                  type="submit"
                  className="bg-violet-600 text-white text-sm font-semibold px-6 py-3 rounded-full shadow-md hover:bg-violet-700 transition"
                  style={{ backgroundColor: "#4b0082" }}
                >
                  Buy Now
                </button>
              </form>
            )}


            {/* ✅ Show Add to Cart only if NOT enrolled */}
            {!isEnrolled && (
              <button
                onClick={handleAddToCart}
                className={`flex items-center gap-2 text-violet-600 bg-violet-100 px-6 py-3 text-sm font-semibold rounded-full hover:bg-violet-200 transition ${inCart ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                disabled={inCart}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M2.25 3h1.636a1.5 1.5 0 011.44 1.09L6.75 6.75m0 0h13.5a.75.75 0 01.75.75v.75a.75.75 0 01-.75.75H7.405m-.655 0L5.25 18.75a.75.75 0 00.75.75h12.75a.75.75 0 00.75-.75V9.75m-11.25 9a1.5 1.5 0 11-3 0m13.5 0a1.5 1.5 0 11-3 0"
                  />
                </svg>
                {inCart ? "Added in Cart" : "Add to Cart"}
              </button>
            )}

            {/* ✅ Show Get Access only if enrolled */}
            {isEnrolled && (
              <button
                onClick={handleGetAccess}
                className="hover:bg-blue-800 text-white font-semibold text-sm px-6 py-3 rounded-md shadow-md"
                style={{ backgroundColor: "rgb(93, 76, 207)" }}
              >
                Get Access
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="py-10 px-6 bg-white">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between">
          <div className="md:w-1/2 mb-10 md:mb-0 text-left">
            <p className="text-sm font-semibold text-gray-700 uppercase mb-3">
              Course Overview
            </p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight mb-5">
              Introduction to <br />
              {course?.course_name}
            </h2>

            <div
              className="text-gray-700 text-base md:text-lg mb-6 text-justify"
              dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
            />
          </div>

          <div className="md:w-1/2 flex justify-center">
            <img
              src="/images/webpage 11-05.webp"
              alt={`${course?.course_name} illustration`}
              className="max-w-full h-auto"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Who Can Enroll?</h2>
          <p className="text-gray-600 text-lg mb-12 max-w-3xl mx-auto">
            This course is ideal for anyone looking to apply data-driven thinking to supply chain management, regardless of their current role or experience level.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 text-left">
            {/* Tile 1 */}
            <div className="bg-white p-6 border border-gray-200 shadow-sm hover:shadow-md transition rounded-md h-full flex flex-col">
              <div className="mb-4">
                <div className="w-12 h-12 bg-indigo-100 text-indigo-700 flex items-center justify-center text-xl font-bold">01</div>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Working Professionals</h3>
              <p className="text-gray-600 text-sm">
                Especially in logistics, procurement, or operations who want to make smarter decisions using analytics.
              </p>
            </div>

            {/* Tile 2 */}
            <div className="bg-white p-6 border border-gray-200 shadow-sm hover:shadow-md transition rounded-md h-full flex flex-col">
              <div className="mb-4">
                <div className="w-12 h-12 bg-green-100 text-green-700 flex items-center justify-center text-xl font-bold">02</div>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Students & Recent Graduates</h3>
              <p className="text-gray-600 text-sm">
                Looking to build marketable skills and stand out with practical analytics knowledge in supply chain domains.
              </p>
            </div>

            {/* Tile 3 */}
            <div className="bg-white p-6 border border-gray-200 shadow-sm hover:shadow-md transition rounded-md h-full flex flex-col">
              <div className="mb-4">
                <div className="w-12 h-12 bg-yellow-100 text-yellow-700 flex items-center justify-center text-xl font-bold">03</div>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Career Switchers</h3>
              <p className="text-gray-600 text-sm">
                Professionals from other fields exploring roles in supply chain management or data analytics.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 ">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-10">
          {/* Text Content */}
          <div className="md:w-1/2 text-left">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Earn a Recognized Certificate</h2>
            <p className="text-gray-600 text-lg mb-6 text-justify">
             Upon successful completion of the Programme, Learners can directly download their Certificate from the LLMS platform. The Certificate serves as official recognition of course completion.
            </p>
            <ul className="space-y-3 text-gray-700 text-base">
              <li className="flex items-start"><span className="text-green-600 mr-2">✔</span> Downloadable in PDF format</li>
              <li className="flex items-start"><span className="text-green-600 mr-2">✔</span> Lifetime validity</li>
             
            </ul>
          </div>

          {/* Certificate Image */}
          <div className="md:w-1/2 flex justify-center">
            <img
              src="/images/Certificate.svg"
              alt="Course Completion Certificate"
              className="max-w-full h-auto "
            />
          </div>
        </div>
      </section>

      {/* ✅ Cart popup */}
      {popup.show && (
        <div className="fixed bottom-4 right-4 bg-violet-600 text-white px-6 py-3 rounded shadow-md z-50">
          <p>
            <strong>{popup.courseName}</strong> has been added to your cart!
          </p>
        </div>
      )}
    </>
  );
}
